import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import type { AnalysisOverviewItem, ProjectInfo } from '@/client/types.gen'

interface WorkspaceProjectsCacheEntry {
	projects: ProjectInfo[]
	totalCount: number | null
	fetchedAtMs: number
}

interface DataRefreshContextType {
	/** Increments every time the project list should be re-fetched. */
	projectsVersion: number
	/** Map of projectId → version counter for that project's analysis list. */
	analysesVersions: Record<string, number>
	/** Cached projects keyed by workspace id to avoid refetching on navigation. */
	projectsByWorkspace: Record<string, WorkspaceProjectsCacheEntry>
	/** Cached analyses keyed by project id to avoid refetching on navigation. */
	analysesByProject: Record<string, AnalysisOverviewItem[]>
	/** Call after any project create / rename / delete. */
	invalidateProjects: () => void
	/** Call after any analysis create / rename / delete for a specific project. */
	invalidateAnalyses: (projectId: string) => void
	/** Store project list cache for a workspace. */
	setProjectsForWorkspace: (
		workspaceId: string,
		projects: ProjectInfo[],
		totalCount: number | null
	) => void
	/** Store analysis list cache for a project. */
	setAnalysesForProject: (projectId: string, analyses: AnalysisOverviewItem[]) => void
}

const DataRefreshContext = createContext<DataRefreshContextType | undefined>(undefined)

export function DataRefreshProvider({ children }: { children: ReactNode }) {
	const [projectsVersion, setProjectsVersion] = useState(0)
	const [analysesVersions, setAnalysesVersions] = useState<Record<string, number>>({})
	const [projectsByWorkspace, setProjectsByWorkspace] = useState<
		Record<string, WorkspaceProjectsCacheEntry>
	>({})
	const [analysesByProject, setAnalysesByProject] = useState<Record<string, AnalysisOverviewItem[]>>(
		{}
	)

	// Clear invalid project list cache and increment version
	const invalidateProjects = useCallback(() => {
		setProjectsVersion((version) => version + 1)
		setProjectsByWorkspace({})
	}, [])

	// Clear invalid analysis list cache and increment version
	const invalidateAnalyses = useCallback((projectId: string) => {
		setAnalysesVersions((previous) => ({
			...previous,
			[projectId]: (previous[projectId] ?? 0) + 1,
		}))
		setAnalysesByProject((prev) => {
			const next = { ...prev }
			delete next[projectId]
			return next
		})
	}, [])

	// Store project list cache for a workspace
	const setProjectsForWorkspace = useCallback(
		(workspaceId: string, projects: ProjectInfo[], totalCount: number | null) => {
			setProjectsByWorkspace((prev) => ({
				...prev,
				[workspaceId]: {
					projects,
					totalCount,
					fetchedAtMs: Date.now(),
				},
			}))
		},
		[]
	)

	// Store analysis list cache for a project
	const setAnalysesForProject = useCallback(
		(projectId: string, analyses: AnalysisOverviewItem[]) => {
			setAnalysesByProject((prev) => ({
				...prev,
				[projectId]: analyses,
			}))
		},
		[]
	)

	const value = useMemo(
		() => ({
			projectsVersion,
			analysesVersions,
			projectsByWorkspace,
			analysesByProject,
			invalidateProjects,
			invalidateAnalyses,
			setProjectsForWorkspace,
			setAnalysesForProject,
		}),
		[
			projectsVersion,
			analysesVersions,
			projectsByWorkspace,
			analysesByProject,
			invalidateProjects,
			invalidateAnalyses,
			setProjectsForWorkspace,
			setAnalysesForProject,
		]
	)

	return <DataRefreshContext.Provider value={value}>{children}</DataRefreshContext.Provider>
}

export function useDataRefresh() {
	const ctx = useContext(DataRefreshContext)
	if (!ctx) throw new Error('useDataRefresh must be used within a DataRefreshProvider')
	return ctx
}
